import { GetterTree, Store } from "vuex";
import { DrinkingState } from "./types";
import { IRootState } from "../types";
import { buildGetter } from "@dcc/store/utils";
import { DrinkFrequency } from "@dcc/types/drinkFrequency";
import {
  DrinksPerDay,
  DrinkingDay,
  DrinksWithTotal
} from "@dcc/types/drinkingDay";
import { drinksPerDayOfweek, sumDrinksForDay } from "./drinksPerDayOfWeek";
import { BingeFrequency } from "@dcc/types/bingeFrequency";
import { drinksOnAverageDay } from "./drinksOnAverageDay";
import { DayOfWeek } from "@dcc/types/dayOfWeek";

enum GetterNames {
  DRINKFREQUENCY = "drinkFrequency",
  MONTHLYDRINKINGDAYS = "monthlyDrinkingDays",
  WEEKLYDRINKINGDAYS = "weeklyDrinkingDays",
  TOTALDRINKSONAWEEKDAY = "drinksOnADay",
  TOTALDRINKSONAVERAGEDAY = "drinksOnAverageDay",
  AVERAGEDRINKINGDAY = "averageDrinks",
  BINGEFREQUENCY = "bingeFrequency",
  SELECTEDDRINKFREEDAYS = "selectedDrinkFreeDays",
  AVAILABLEDRINKFREEDAYS = "availableDrinkFreeDays",
  ACTUALDRINKINGDAYS = "actualDrinkingDays",
  ORIGINALDRINKINGDAYS = "originalDrinkingDays",
  TARGETPERCENTAGE = "targetPercentage"
}

export interface DrinkingGetters {
  [GetterNames.TOTALDRINKSONAWEEKDAY]: DrinksPerDay[];
  [GetterNames.SELECTEDDRINKFREEDAYS]: DayOfWeek[];
  [GetterNames.DRINKFREQUENCY]: DrinkFrequency | undefined;
  [GetterNames.AVERAGEDRINKINGDAY]: DrinkingDay;
  [GetterNames.WEEKLYDRINKINGDAYS]: number[];
}

export const getters: GetterTree<DrinkingState, IRootState> = {
  [GetterNames.DRINKFREQUENCY](
    state: DrinkingState
  ): DrinkFrequency | undefined {
    return state.drinkFrequency;
  },
  [GetterNames.MONTHLYDRINKINGDAYS](state: DrinkingState): number {
    return state.monthlyDrinkingDays;
  },
  [GetterNames.WEEKLYDRINKINGDAYS](state: DrinkingState): number[] {
    return Object.keys(state.drinkingDays).map((k) => Number(k));
  },
  [GetterNames.TOTALDRINKSONAWEEKDAY](state: DrinkingState): DrinksPerDay[] {
    return drinksPerDayOfweek(state.drinkingDays);
  },
  [GetterNames.TOTALDRINKSONAVERAGEDAY](state: DrinkingState): number {
    return drinksOnAverageDay(state.averageDrinkingDay);
  },
  [GetterNames.BINGEFREQUENCY](
    state: DrinkingState
  ): BingeFrequency | undefined {
    return state.bingeFrequency;
  },
  [GetterNames.AVERAGEDRINKINGDAY](state: DrinkingState): DrinkingDay {
    return state.averageDrinkingDay;
  },
  [GetterNames.SELECTEDDRINKFREEDAYS](state: DrinkingState): DayOfWeek[] {
    return state.drinkFreeDays;
  },
  [GetterNames.AVAILABLEDRINKFREEDAYS](
    state: DrinkingState,
    getters: DrinkingGetters
  ): DayOfWeek[] {
    const selectedDays = getters[GetterNames.WEEKLYDRINKINGDAYS];

    return [
      DayOfWeek.Monday,
      DayOfWeek.Tuesday,
      DayOfWeek.Wednesday,
      DayOfWeek.Thursday,
      DayOfWeek.Friday,
      DayOfWeek.Saturday,
      DayOfWeek.Sunday
    ].filter((val) => selectedDays.includes(val));
  },
  [GetterNames.ACTUALDRINKINGDAYS](
    state: DrinkingState,
    getters: DrinkingGetters
  ): DrinksWithTotal[] {
    const drinkFrequency = getters["drinkFrequency"];

    if (drinkFrequency && drinkFrequency === DrinkFrequency.Weekly) {
      const weeklyDrinkingDays = getters[GetterNames.TOTALDRINKSONAWEEKDAY];
      const selectedDrinkFreeDays = getters[GetterNames.SELECTEDDRINKFREEDAYS];
      return weeklyDrinkingDays.filter(
        (d) => !selectedDrinkFreeDays.includes(d.day)
      );
    } else if (
      drinkFrequency &&
      (drinkFrequency === DrinkFrequency.Monthly ||
        drinkFrequency === DrinkFrequency.TwiceMonthly)
    ) {
      const averageDrinks = getters[GetterNames.AVERAGEDRINKINGDAY];
      return [{ ...averageDrinks, amount: sumDrinksForDay(averageDrinks) }];
    }
    return [];
  },
  [GetterNames.ORIGINALDRINKINGDAYS](
    state: DrinkingState,
    getters: DrinkingGetters
  ): DrinksWithTotal[] {
    const drinkFrequency = getters["drinkFrequency"];

    if (drinkFrequency && drinkFrequency === DrinkFrequency.Weekly) {
      return getters[GetterNames.TOTALDRINKSONAWEEKDAY];
    } else if (
      drinkFrequency &&
      (drinkFrequency === DrinkFrequency.Monthly ||
        drinkFrequency === DrinkFrequency.TwiceMonthly)
    ) {
      const averageDrinks = getters[GetterNames.AVERAGEDRINKINGDAY];
      return [{ ...averageDrinks, amount: sumDrinksForDay(averageDrinks) }];
    }
    return [];
  },
  [GetterNames.TARGETPERCENTAGE](state: DrinkingState): number | undefined {
    return state.targetPercentage;
  }
};

export const findGetters = (store: Store<IRootState>) => {
  return {
    getDrinkFrequency: () =>
      buildGetter<DrinkFrequency | undefined>(
        store,
        "drinking",
        GetterNames.DRINKFREQUENCY
      ),
    getNumberMonthlyDrinkingDays: () =>
      buildGetter<number>(store, "drinking", GetterNames.MONTHLYDRINKINGDAYS),
    getWeeklyDrinkingDays: () =>
      buildGetter<number[]>(store, "drinking", GetterNames.WEEKLYDRINKINGDAYS),
    getTotalDrinksOnWeekday: () =>
      buildGetter<DrinksPerDay[]>(
        store,
        "drinking",
        GetterNames.TOTALDRINKSONAWEEKDAY
      ),
    getTotalDrinksOnAverageDay: () =>
      buildGetter<number>(
        store,
        "drinking",
        GetterNames.TOTALDRINKSONAVERAGEDAY
      ),
    getBingeFrequency: () =>
      buildGetter<BingeFrequency | undefined>(
        store,
        "drinking",
        GetterNames.BINGEFREQUENCY
      ),
    getDrinksOnAverageDay: () =>
      buildGetter<DrinkingDay>(
        store,
        "drinking",
        GetterNames.AVERAGEDRINKINGDAY
      ),
    getSelectedDrinkFreeDays: () =>
      buildGetter<DayOfWeek[]>(
        store,
        "drinking",
        GetterNames.SELECTEDDRINKFREEDAYS
      ),
    getAvailableDrinkFreeDays: () =>
      buildGetter<DayOfWeek[]>(
        store,
        "drinking",
        GetterNames.AVAILABLEDRINKFREEDAYS
      ),
    getTargetPercentage: () =>
      buildGetter<number>(store, "drinking", GetterNames.TARGETPERCENTAGE)
  };
};
